import { ColDef, ColGroupDef } from "ag-grid-community";

import { aggridHeaderClass } from "src/components/common/common";
import { RaonColDef } from "src/types/grid";
import { getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";

import {
	TQC2090_R01Dto,
	TQC2090_R01_VALUE_FIELDS,
} from "../Dto_TQC2090_R01";

export const TQC2090_R01_READONLY_COLUMNS = [
	"dayOfMonth", "dayOfWeekNm", "inspItemNm", "specText",
	"thicknessItemNm", "thicknessSpecText",
];

export const TQC2090_R01_HEADER_NAMES: Record<string, string> = {
	rowIndex: "",
	dayOfMonth: "일자",
	dayOfWeekNm: "요일",
	inspItemNm: "검사항목",
	specText: "기준값",
	thicknessItemNm: "검사항목",
	thicknessSpecText: "기준값",
	thicknessFirstValue: "초품",
	thicknessMiddleValue: "중품",
	thicknessLastValue: "종품",
	remark: "비고",
	inspectValue1_1: "값1", inspectValue1_2: "값2", inspectValue1_3: "값3",
	inspectValue1_4: "값4", inspectValue1_5: "값5",
	inspectValue2_1: "값1", inspectValue2_2: "값2", inspectValue2_3: "값3",
	inspectValue2_4: "값4", inspectValue2_5: "값5",
	inspectValue3_1: "값1", inspectValue3_2: "값2", inspectValue3_3: "값3",
	inspectValue3_4: "값4", inspectValue3_5: "값5",
};

const VALUE_GROUPS = [
	{ groupName: "초품", prefix: "inspectValue1_" },
	{ groupName: "중품", prefix: "inspectValue2_" },
	{ groupName: "종품", prefix: "inspectValue3_" },
] as const;

const valueColumn = (
	field: string,
	headerName: string,
): RaonColDef<TQC2090_R01Dto> => ({
	headerName,
	colId: field,
	field: field as keyof TQC2090_R01Dto,
	width: 80,
	editable: true,
	pasteable: true,
});

export const getTQC2090_R01ColumnDefinitions = (
	isNickelLine: boolean,
): (ColDef<TQC2090_R01Dto> | ColGroupDef<TQC2090_R01Dto>)[] => {
	const leadingColumns: ColDef<TQC2090_R01Dto>[] = [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "일자", colId: "dayOfMonth", field: "dayOfMonth",
			editable: false,
		},
		{
			headerName: "요일", colId: "dayOfWeekNm", field: "dayOfWeekNm", width: 70,
			editable: false,
		},
	];

	const valueColumns = isNickelLine
		? getNickelColumns()
		: getZincColumns();
	const remarkColumn = {
		headerName: isNickelLine ? "품명" : "비고",
		colId: "remark", field: "remark", width: 220,
		editable: true, pasteable: true,
	} as RaonColDef<TQC2090_R01Dto>;

	return [
		...leadingColumns,
		...(isNickelLine ? [remarkColumn] : []),
		...valueColumns,
		...(!isNickelLine ? [remarkColumn] : []),
	];
};

const getNickelColumns = (): ColDef<TQC2090_R01Dto>[] => [
	{
		headerName: "검사항목", colId: "inspItemNm", field: "inspItemNm", width: 110,
		editable: false,
	},
	{
		headerName: "기준값", colId: "specText", field: "specText",
		editable: false,
	},
	valueColumn("inspectValue1_1", "초품"),
	valueColumn("inspectValue2_1", "중품"),
	valueColumn("inspectValue3_1", "종품"),
	{
		headerName: "검사항목", colId: "thicknessItemNm", field: "thicknessItemNm", width: 110,
		editable: false,
	},
	{
		headerName: "기준값", colId: "thicknessSpecText", field: "thicknessSpecText",
		editable: false,
	},
	valueColumn("thicknessFirstValue", "초품"),
	valueColumn("thicknessMiddleValue", "중품"),
	valueColumn("thicknessLastValue", "종품"),
];

const getZincColumns = (): (ColDef<TQC2090_R01Dto> | ColGroupDef<TQC2090_R01Dto>)[] => [
	{
		headerName: "검사항목", colId: "inspItemNm", field: "inspItemNm", width: 140,
		editable: false,
	},
	...VALUE_GROUPS.map((group) => ({
		headerName: group.groupName,
		headerClass: "common-group-header",
		children: [1, 2, 3, 4, 5].map((seq) =>
			valueColumn(`${group.prefix}${seq}`, `값${seq}`),
		),
	})),
];

export const normalizeTQC2090_R01Headers = (
	defs: (ColDef<TQC2090_R01Dto> | ColGroupDef<TQC2090_R01Dto>)[],
): (ColDef<TQC2090_R01Dto> | ColGroupDef<TQC2090_R01Dto>)[] =>
	defs.map((def) => {
		if ("children" in def && Array.isArray(def.children)) {
			return {
				...def,
				headerClass: [def.headerClass, "common-group-header"].filter(Boolean).join(" "),
				suppressMenu: true,
				children: normalizeTQC2090_R01Headers(def.children),
			};
		}
		const column = def as ColDef<TQC2090_R01Dto>;
		return {
			...column,
			headerClass: [aggridHeaderClass, column.headerClass].filter(Boolean).join(" "),
			suppressMenu: true,
		};
	});

export const getTQC2090_R01EditableColumns = (isNickelLine: boolean): string[] =>
	isNickelLine
		? [
			"inspectValue1_1", "inspectValue2_1", "inspectValue3_1",
			"thicknessFirstValue", "thicknessMiddleValue", "thicknessLastValue",
			"remark",
		]
		: [...TQC2090_R01_VALUE_FIELDS, "remark"];

export const getTQC2090_R01GridOptions = () => ({
	ensureDomOrder: true,
	suppressRowHoverHighlight: true,
	pagination: false,
	maxBlocksInCache: 10,
	blockLoadDebounceMillis: 100,
	suppressBrowserResizeObserver: true,
	suppressPropertyNamesCheck: true,
	debounceVerticalScrollbar: true,
	debounceHorizontalScrollbar: true,
	suppressCellChangeFlash: true,
});
